import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Bell, MessageSquare, ChevronDown, Menu, LogOut, User, Loader2 } from 'lucide-react';
import styles from './Header.module.css';
import api, { getStorageUrl } from '../../../api/axios';

const Header = ({ onMenuClick }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isEmployee = user.role?.toLowerCase() !== 'admin';

  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const response = await api.get('/overtime/notifications', { skipLoader: true });
      if (response.data && response.data.success) {
        const rawData = response.data.data?.data || response.data.data || [];
        setNotifications(rawData);
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    if (isDropdownOpen || isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isNotificationOpen]);

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await api.put(`/overtime/notifications/${notif.id}/read`, {}, { skipLoader: true });
        fetchNotifications();
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
    setIsNotificationOpen(false);

    const data = notif.data || {};
    const type = notif.type;

    if (isEmployee) {
      if (type === 'assignment') {
        if (data.assignment_id) {
          navigate(`/employees/ot-assignments/view/${data.assignment_id}`);
        } else {
          navigate('/employees/my-overtime?tab=assignments');
        }
      } else if (type && type.startsWith('request')) {
        const searchParam = data.request_id ? `&search=${data.request_id}` : '';
        navigate(`/employees/my-overtime?tab=requests${searchParam}`);
      } else {
        navigate('/employees/my-overtime');
      }
    } else {
      // Admin/Manager
      if (type === 'assignment') {
        if (data.assignment_id) {
          navigate(`/employees/ot-assignments/view/${data.assignment_id}`);
        } else {
          navigate('/employees/ot-assignments');
        }
      } else if (type && type.startsWith('request')) {
        const searchParam = data.request_id ? `&search=${data.request_id}` : '';
        navigate(`/employees/ot-requests?tab=requests${searchParam}`);
      } else {
        navigate('/employees/ot-assignments');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.clear();
      navigate('/login');
    }
  };

  return (
    <header className={styles['dashboard-header']}>
      <div className={styles['header-left']}>
        <button className={styles['menu-toggle-btn']} onClick={onMenuClick}>
          <Menu size={24} />
        </button>
        {/* <div className={styles['search-container']}>
          <Search className={styles['search-icon']} size={20} />
          <input 
            type="text" 
            placeholder="Search projects, tasks, or POs..." 
            className={styles['search-input']} 
          />
        </div> */}
      </div>


      <div className={styles['header-actions']}>
        <div ref={notificationRef} className={styles['notification-container']}>
          <button 
            className={styles['icon-btn']} 
            onClick={() => {
              const nextState = !isNotificationOpen;
              setIsNotificationOpen(nextState);
              if (nextState) {
                fetchNotifications();
              }
            }}
          >
            <Bell size={20} />
            {unreadCount > 0 && <span className={styles['badge']}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>

          {isNotificationOpen && (
            <div className={styles['notification-dropdown']}>
              <div className={styles['notification-header']}>
                <h3>Notifications ({unreadCount})</h3>
              </div>
              {notificationsLoading ? (
                <div className={styles['no-notifications']} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '24px 12px' }}>
                  <Loader2 className={styles['spinner']} size={16} />
                  <span>Loading notifications...</span>
                </div>
              ) : notifications.length === 0 ? (
                <div className={styles['no-notifications']}>
                  No overtime notifications
                </div>
              ) : (
                notifications.map((notif) => (
                  <button
                    key={notif.id}
                    className={`${styles['notification-item']} ${!notif.is_read ? styles['unread'] : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <span className={styles['notification-title']}>{notif.title}</span>
                    <span className={styles['notification-message']}>{notif.message}</span>
                    <span className={styles['notification-time']}>
                      {new Date(notif.created_at).toLocaleDateString()} {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div
          ref={dropdownRef}
          className={styles['user-profile']}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <div className={styles['user-info']}>
            <span className={styles['user-name']}>{user?.name || 'User'}</span>
            <span className={styles['user-role']}>{user?.role || 'Project Manager'}</span>
          </div>
          <div className={styles['user-avatar']}>
            <img 
              src={user?.profile_image ? getStorageUrl(user.profile_image) : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=ff9d42&color=fff`} 
              alt="User Avatar" 
            />
          </div>
          <ChevronDown size={16} className={`${styles['chevron']} ${isDropdownOpen ? styles['open'] : ''}`} />

          {isDropdownOpen && (
            <div className={styles['dropdown-menu']}>
              <Link to="/profile" className={styles['dropdown-item']}>
                <User size={16} />
                <span>My Profile</span>
              </Link>
              <div className={styles['dropdown-divider']}></div>
              <button className={`${styles['dropdown-item']} ${styles['logout']}`} onClick={handleLogout}>
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
